import { computed, type ComputedRef } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { getArticleRating, rateArticle } from '@/common/api/api.pleased';
import { useBotUser } from '@/stores/useBotUser';
import { ArticleRate } from '@/common/api/models.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';

/**
 * TanStack Query hook to fetch article rating
 * Only fetches when user is not a guest (logged in)
 * Provides automatic caching, loading states, and error handling
 *
 * @param articleId - Article ID to fetch rating for
 * @returns TanStack Query result with rating data
 *
 * @example
 * ```ts
 * const articleId = computed(() => Number(route.params.id));
 * const { data: ratingData } = useArticleRating(articleId);
 * const currentRating = computed(() => ratingData.value?.rating || null);
 * ```
 */
export const useArticleRating = (articleId: ComputedRef<number | undefined>) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive } = storeToRefs(mockedFlowStore);

  const { user } = storeToRefs(useBotUser());
  const isLoggedIn = computed(() => !user.value.isGuest);

  return useQuery({
    queryKey: ['articleRating', articleId],
    queryFn: async () => {
      if (isMockedFlowActive.value)
        return {
          rating: ArticleRate.HELPFUL,
        };

      if (!articleId.value) {
        throw new Error('Article ID is required');
      }
      const response = await getArticleRating({ articleId: articleId.value });
      return response.data;
    },
    enabled: computed(() => !!articleId.value && isLoggedIn.value),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: false, // Don't retry on failure - rating might not exist yet
  });
};

/**
 * TanStack Mutation hook to rate an article
 * Automatically updates the query cache after successful rating
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```ts
 * const { mutate: rateArticle } = useRateArticle();
 *
 * const handleRate = (rating: ArticleRate) => {
 *   rateArticle({ articleId: 123, rating });
 * };
 * ```
 */
export const useRateArticle = () => {
  const queryClient = useQueryClient();
  const { user } = storeToRefs(useBotUser());
  const isLoggedIn = computed(() => !user.value.isGuest);

  return useMutation({
    mutationFn: async ({ articleId, rating }: { articleId: number; rating: ArticleRate }) => {
      const response = await rateArticle({ articleId, rating });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Only update the query cache if user is logged in
      // Guest users can rate but can't fetch their rating
      if (isLoggedIn.value) {
        // Update the query cache with the new rating
        // Use the actual articleId value to match the query key structure
        queryClient.setQueryData(['articleRating', variables.articleId], data);
      }
    },
  });
};
